import * as React from 'react';
import {Dispatch} from "redux";
import {connect} from "react-redux";

import {RouteComponentProps, withRouter} from "react-router-dom";
import {AppState} from "../store";
import {IdObjectsWithId} from "../utils";
import {FileType, ICheckedFileIds, IFile} from "../store/files/types";
import {
    addFileAsyncActions, checkFilesAsyncAction,
    deleteFileAsyncActions, fetchAllFilesAsyncActions,
    fetchFileChildrenAsyncActions,
    IAddFileAsyncRequestPayload, ICheckFilesRequestActionPayload,
    IDeleteFileAsyncRequestPayload,
    IFetchFileChildrenAsyncRequestPayload, IMoveFileAsyncRequestPayload,
    IRenameFileAsyncRequestPayload,
    ISelectFileActionPayload, moveFileAsyncActions,
    renameFileAsyncActions,
    selectFileAsyncAction
} from "../store/files/actions";
import {
    selectCheckedFileIds,
    selectFiles,
    selectIsFilesLoading, selectIsFileSystemLoading,
    selectRoot,
    selectSelectedFile,
} from "../store/files/selectors";
import FilesTree, {FilesTreeCheckedHandler, IFilesTreeProps, isCheckedKeys} from "../components/FileSystem/FilesTree";
import SplitPane from "react-split-pane";
import {Row, Spin} from "antd";
import FileDataContainer from "./FileDataContainer";
import {IBannerTemplate} from "../store/banner/template/types";
import {modifyingActiveBannerTemplateAction} from "../store/banner/template/actions";
import {selectActiveBannerTemplate} from "../store/banner/template/selectors";
import BasicLayout from "../layout/BasicLayout";

interface IMatchParams {
}

interface IOwnProps extends RouteComponentProps<IMatchParams> {
}

interface IStateProps {
    activeBannerTemplate: IBannerTemplate;
    files: IdObjectsWithId<IFile>;
    root: IFile | undefined;
    isFilesLoading: boolean;
    isFileSystemLoading: boolean;
    checkedFileIds: ICheckedFileIds;
    selectedFile?: IFile;
}

interface IDispatchProps {
    fetchAllFiles: () => void
    fetchChildren: (params: IFetchFileChildrenAsyncRequestPayload) => void
    addFile: (params: IAddFileAsyncRequestPayload) => void
    renameFile: (params: IRenameFileAsyncRequestPayload) => void;
    moveFile: (params: IMoveFileAsyncRequestPayload) => void;
    deleteFile: (params: IDeleteFileAsyncRequestPayload) => void;
    selectFile: (params: ISelectFileActionPayload) => void;
    checkFiles: (params: ICheckFilesRequestActionPayload) => void;
    modifyingActiveBannerTemplate: (value: Partial<IBannerTemplate>) => void;
}

type IProps = IOwnProps & IStateProps & IDispatchProps;


const mapStateToProps = (state: AppState, props: IOwnProps): IStateProps => ({
    activeBannerTemplate: selectActiveBannerTemplate(state),
    files: selectFiles(state),
    root: selectRoot(state),
    isFilesLoading: selectIsFilesLoading(state),
    isFileSystemLoading: selectIsFileSystemLoading(state),
    checkedFileIds: selectCheckedFileIds(state),
    selectedFile: selectSelectedFile(state),
});

const mapDispatchToProps = (
    dispatch: Dispatch
): IDispatchProps => {
    return {
        fetchAllFiles: () => dispatch(fetchAllFilesAsyncActions.request({})),
        fetchChildren: (params: IFetchFileChildrenAsyncRequestPayload) => dispatch(fetchFileChildrenAsyncActions.request(params)),
        addFile: (params: IAddFileAsyncRequestPayload) => dispatch(addFileAsyncActions.request(params)),
        renameFile: (params: IRenameFileAsyncRequestPayload) => dispatch(renameFileAsyncActions.request(params)),
        moveFile: (params: IMoveFileAsyncRequestPayload) => dispatch(moveFileAsyncActions.request(params)),
        deleteFile: (params: IDeleteFileAsyncRequestPayload) => dispatch(deleteFileAsyncActions.request(params)),
        selectFile: (params: ISelectFileActionPayload) => dispatch(selectFileAsyncAction.request(params)),
        checkFiles: (params: ICheckFilesRequestActionPayload) => dispatch(checkFilesAsyncAction.request(params)),
        modifyingActiveBannerTemplate: (value: Partial<IBannerTemplate>) => dispatch(modifyingActiveBannerTemplateAction({bannerTemplate: value})),
    };
};

interface IState {
    colSize: number;
}

class FileSystemContainer extends React.PureComponent<IProps, IState> {
    state: IState = {
        colSize: 0
    };
    onFetchChildren = (id: string, resolve: (value?: any | PromiseLike<void> | undefined) => void) => {
        if (this.props.fetchChildren && id) {
            const file = this.props.files.byId[id];
            if (file) {
                this.props.fetchChildren({file: file, resolve: resolve})
            }
        }
    };

    onAddFile = (params: IAddFileAsyncRequestPayload) => {
        if (this.props.addFile) {
            this.props.addFile(params)
        }
    };
    onDeleteFile = (file: IFile) => {
        if (this.props.deleteFile) {
            this.props.deleteFile({file: file})
        }
    };

    onRenameFile = (file: IFile, newName: string) => {
        if (this.props.renameFile) {
            this.props.renameFile({file: file, name: newName})
        }
    };

    onMoveFile = (file: IFile, newParentPath: string) => {
        if (this.props.moveFile) {
            this.props.moveFile({file: file, parentPath: newParentPath})
        }
    };

    onSelectFileName = (file: IFile) => {
        this.props.selectFile({file: file})
    };

    onCheckFiles = (params: FilesTreeCheckedHandler) => {
        if (!this.props.files) return;

        // last checked node
        const id = params.info.node.props.eventKey;
        if (id) {
            let ids: string[] = [];
            const file = this.props.files.byId[id];
            if (!file) return;
            if (file.type === FileType.FILE) return;

            this.props.checkFiles({folderIds: [id], disableUncheck: false})
        }
    };

    onResize = (newSize: number) => {
        this.setState({
            colSize: newSize
        })
    };


    render() {
        // console.log("FileSystemContainer render........");
        let filesTreeProps: IFilesTreeProps = {
            files: this.props.files,
            root: this.props.root,
            checkedFileIds: this.props.checkedFileIds,
            onAddFile: this.onAddFile,
            onDeleteFile: this.onDeleteFile,
            onRenameFile: this.onRenameFile,
            onMoveFile: this.onMoveFile,
            onFetchChildren: this.onFetchChildren,
            onSelectFileName: this.onSelectFileName,
            onCheckFiles: this.onCheckFiles,
        };
        return (
            <React.Fragment>
                {!this.props.isFileSystemLoading && <Row style={{height: "100vh", width: "100%", display: "relative"}}>
                    <SplitPane
                        split="vertical"
                        minSize={0}
                        maxSize={-13}
                        defaultSize={"35%"}
                        onDragFinished={this.onResize}
                        pane1Style={{paddingRight: "10px"}}
                        pane2Style={{paddingLeft: "10px", width: "100%", overflow: "scroll"}}
                    >
                        <div>
                            <FilesTree {...filesTreeProps}/>
                        </div>
                        <div>
                            {this.props.selectedFile &&
                            <FileDataContainer size={this.state.colSize}/>
                            }
                        </div>
                    </SplitPane>
                </Row>
                }
            </React.Fragment>
        )
    }
}


export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps,
)(FileSystemContainer));
